import { AxiosError } from 'axios';
import { TFunction } from 'i18next';

export type SignUpErrorResponse = {
  error?: {
    code?: number;
    message?: string;
  };
};

export type SignUpSuccessResponse = {
  existing_firebase_account?: boolean;
};

const SIGNUP_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'user already exists': 'sign_up_error_tenant_duplicate',
  'invalid request format': 'sign_up_error',
  'auth/invalid-email': 'invalid_email_error',
  'auth/weak-password': 'weak_password_error',
  'auth/internal-error': 'server_error',
  'mentor self-registration disabled': 'mentor_self_registration_disabled',
  'event intake disabled': 'event_intake_disabled',
  'guest cannot be mentor': 'guest_cannot_be_mentor',
  'captcha required': 'sign_up_error_captcha',
  'captcha verification failed': 'sign_up_error_captcha',
  'captcha failed': 'sign_up_error_captcha',
  'captcha hostname mismatch': 'sign_up_error_captcha',
};

export const parseSignUpErrorResponse = (data: unknown): SignUpErrorResponse['error'] | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const errorData = data as SignUpErrorResponse;
  const message = errorData.error?.message?.trim();
  if (!message) {
    return null;
  }

  return {
    code: errorData.error?.code,
    message,
  };
};

export const resolveSignUpErrorMessage = (
  error: unknown,
  t: TFunction<'translation', undefined>,
): string | null => {
  if (!(error instanceof AxiosError) || !error.response?.data) {
    return t('notification_messages.unexpected_error');
  }

  const parsed = parseSignUpErrorResponse(error.response.data);
  if (!parsed?.message) {
    return t('notification_messages.unexpected_error');
  }

  const messageKey = SIGNUP_ERROR_MESSAGE_KEYS[parsed.message];
  if (messageKey) {
    return t(`notification_messages.${messageKey}`);
  }

  return parsed.message;
};

export const parseSignUpSuccessResponse = (data: unknown): SignUpSuccessResponse | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const successData = data as SignUpSuccessResponse;
  if (typeof successData.existing_firebase_account !== 'boolean') {
    return null;
  }

  return {
    existing_firebase_account: successData.existing_firebase_account,
  };
};

export const isExistingFirebaseSignUpSuccess = (data: unknown): boolean =>
  parseSignUpSuccessResponse(data)?.existing_firebase_account === true;

export const isDuplicateEmailSignUpError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError) || !error.response?.data) {
    return false;
  }

  const parsed = parseSignUpErrorResponse(error.response.data);
  return parsed?.message === 'user already exists';
};
